import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  authApi,
  fetchCsrfToken,
  clearCsrfToken,
} from '../services/api.client';
import { useToast } from './ToastContext';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  clearError: () => void;
  hasStoredToken: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const authCheckInProgressRef = React.useRef(false);
  const { showWarning, showError } = useToast();

  // Security Note: Currently using sessionStorage (expires on tab close)
  // and localStorage (only with explicit user consent via "Remember Me").
  // TODO: Implement httpOnly cookies with secure API endpoints for production.

  // Helper function to get the current valid token
  const getValidToken = (): string | null => {
    const sessionToken = sessionStorage.getItem('adminToken');
    const persistedToken = localStorage.getItem('adminToken');
    return sessionToken || persistedToken;
  };

  // Helper function to validate if we have any stored tokens
  const hasStoredToken = (): boolean => {
    return !!(
      sessionStorage.getItem('adminToken') || localStorage.getItem('adminToken')
    );
  };

  const checkAuth = async (retryCount = 0) => {
    // Prevent duplicate concurrent auth checks (especially from React StrictMode)
    if (authCheckInProgressRef.current) {
      return;
    }

    authCheckInProgressRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Check if we have any stored tokens
      const token = getValidToken();

      if (token) {
        // Validate token with API and get user data
        const userData = await authApi.getMe();

        if (userData) {
          setUser(userData);

          // Ensure token is available in sessionStorage for this session
          const sessionToken = sessionStorage.getItem('adminToken');
          if (!sessionToken) {
            sessionStorage.setItem('adminToken', token);
          }

          // Fetch CSRF token after successful authentication
          try {
            await fetchCsrfToken();
          } catch (error) {
            console.error(
              'Failed to fetch CSRF token during auth check:',
              error
            );
            // Non-fatal error - continue with authentication
          }
        } else {
          // Token is invalid, clear it
          sessionStorage.removeItem('adminToken');
          localStorage.removeItem('adminToken');
          clearCsrfToken();
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);

      // Only retry on network errors, not on 401/403 which indicate invalid tokens
      const isNetworkError =
        error instanceof Error &&
        (error.message.includes('fetch') ||
          error.message.includes('network') ||
          error.message.includes('timeout') ||
          error.message.includes('500') ||
          error.message.includes('502') ||
          error.message.includes('503') ||
          error.message.includes('504') ||
          error.name === 'TypeError' ||
          error.name === 'NetworkError');

      // Retry up to 2 times for network errors
      if (isNetworkError && retryCount < 2) {
        console.log(`Retrying auth check (attempt ${retryCount + 1}/2)...`);
        setTimeout(() => checkAuth(retryCount + 1), 1000 * (retryCount + 1)); // Progressive delay
        return;
      }

      // Check if it's strictly an authentication error (401/403)
      const isAuthError =
        error instanceof Error &&
        (error.message.includes('401') ||
          error.message.includes('unauthorized') ||
          error.message.includes('invalid'));

      if (isAuthError) {
        console.log('Clearing tokens due to authentication error');
        sessionStorage.removeItem('adminToken');
        localStorage.removeItem('adminToken');
        clearCsrfToken();
        setUser(null);
        showError('Session expired. Please login again.');
      } else {
        // For server errors (500) or network issues that persist after retries
        // DO NOT logout the user. Keep their local state so they don't lose work.
        console.warn(
          'Temporary connection/server error during auth check. Preserving session.'
        );

        // Extract error message for the toast
        const errorMessage =
          error instanceof Error ? error.message : 'Connection error';

        // Show a warning instead of error to indicate the session is still locally active
        showWarning(`Connection unstable: ${errorMessage}. Reconnecting...`);

        // We do NOT set user(null) here, keeping the interface active.
      }

      // Don't set error for initial auth check failures
      // This prevents showing errors on page load
    } finally {
      setLoading(false);
      authCheckInProgressRef.current = false;
    }
  };

  useEffect(() => {
    // Validate authentication on app initialization
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Set up periodic token validation in a separate effect
  useEffect(() => {
    const intervalId = setInterval(
      () => {
        const token = getValidToken();
        if (token && user) {
          console.log('Performing periodic token validation...');
          checkAuth();
        }
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Re-run when user changes to update the interval

  // Listen for storage events to sync auth state across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'adminToken') {
        if (e.newValue === null) {
          // Token was removed in another tab - logout this tab too
          console.log('Token removed in another tab, logging out...');
          clearCsrfToken();
          setUser(null);
        } else if (e.newValue && !user) {
          // Token was added in another tab - validate it
          console.log('Token added in another tab, validating...');
          checkAuth();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const login = async (email: string, password: string, rememberMe = false) => {
    setError(null);

    try {
      console.log('Attempting login with:', { email, password: '***' });
      const response = await authApi.login(email, password);
      console.log('Login successful:', {
        response,
        user: response.user,
        userRole: response.user?.role,
        token: response.token ? 'Token received' : 'No token',
      });

      // Store token based on remember me preference
      sessionStorage.setItem('adminToken', response.token);

      if (rememberMe) {
        // Only use localStorage if explicitly requested
        localStorage.setItem('adminToken', response.token);
      } else {
        // Clear any existing persisted token
        localStorage.removeItem('adminToken');
      }

      setUser(response.user);
      console.log('User state updated, login complete');

      // Fetch CSRF token after successful login (non-blocking)
      fetchCsrfToken().catch((error) => {
        console.error('Failed to fetch CSRF token after login:', error);
        // Non-fatal error - continue with login
      });
    } catch (error) {
      console.error('Login failed with error:', error);

      // Clear any existing tokens on login failure
      sessionStorage.removeItem('adminToken');
      localStorage.removeItem('adminToken');
      clearCsrfToken();

      // Set error for display
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name,
        });
        setError(error.message);
      } else {
        setError('Login failed. Please try again.');
      }

      throw error;
    }
  };

  const logout = async () => {
    setError(null);

    try {
      // Clear tokens immediately to prevent further API calls with expired token
      const currentToken = getValidToken();
      sessionStorage.removeItem('adminToken');
      localStorage.removeItem('adminToken');
      clearCsrfToken();
      setUser(null);

      // Then call logout API endpoint (if we had a token)
      if (currentToken) {
        await authApi.logout();
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // This is fine - we already cleared local state
      // Set error but don't throw - logout should always succeed locally
      if (error instanceof Error) {
        setError(error.message);
      }
    }
  };

  const clearError = () => {
    setError(null);
  };

  // Manual refresh function that forces a new auth check
  const refreshAuth = async () => {
    if (hasStoredToken()) {
      await checkAuth(0); // Force a fresh check without retrying
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    refreshAuth,
    clearError,
    hasStoredToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
